import {HttpErrorResponse} from '@angular/common/http';
import {Component, OnDestroy, OnInit} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {ActivatedRoute} from '@angular/router';
import {Breadcrumb} from '@app/models/breadcrumb.model';
import {PeopleHRAPIKey} from '@app/models/integrations/people-hr/people-hr-api-key.model';
import {PeopleHRSyncToken} from '@app/models/integrations/people-hr/people-hr-sync-token.model';
import {PeopleHRAPIService} from '@app/shared/api/people-hr.api.service';
import {BreadcrumbService} from '@app/shared/breadcrumbs/breadcrumbs.service';
import {SwalUtils} from '@app/shared/utils/swal.utils';
import {environment} from 'environments/environment';
import {forkJoin} from 'rxjs';
import {finalize} from 'rxjs/operators';

interface PeopleHRAPIResponse {
  isError: boolean,
  Status: PeopleHRAPIResponseStatus,
  Message: string,
  Result: any
  // {"isError":true,"Status":2,"Message":"Invalid API Key.","Result":{}}
}

enum PeopleHRAPIResponseStatus {
  INVALID_API_KEY = 2,
  INVALID_CREDENTIALS = 5
}

enum WorkflowState {
  INITIAL,
  SETUP,
  SYNCING,
  COMPLETE,
  UPDATE
}

@Component({
  selector: 'app-integration-people-hr',
  templateUrl: './integration-people-hr.component.html',
  styleUrls: ['./integration-people-hr.component.scss']
})
export class IntegrationPeopleHRComponent implements OnInit, OnDestroy {
  private readonly breadcrumb: Breadcrumb;
  readonly syncURL: string = 'https://api.frankli.io/people-hr/events?token=';
  readonly eWorkflowState = WorkflowState;
  state: WorkflowState = WorkflowState.INITIAL;
  loadingSetup: boolean = false;

  get webhookURL(): string {
    if (this.syncToken) {
      return `${environment.apiUrl}/people-hr/events/${this.syncToken.token}`;
    }
    return 'error';
  }

  // set up form
  setupForm: FormGroup
  setupSubmitted: boolean = false;
  setupApiKeyInvalid: string | null = null; // tracks if the key was flagged as invalid and hasn't been changed in the form since
  setupCompleting: boolean = false;
  syncTokenCopied: boolean = false;

  // complete state
  apiKey: PeopleHRAPIKey | null = null;
  syncToken: PeopleHRSyncToken | null = null;
  webhookUrlCopied: boolean = false;
  apiKeyCopied: boolean = false;
  loadingDisableSync: boolean = false;

  loadingManualSync: boolean = false;

  constructor(
    private breadcrumbService: BreadcrumbService,
    public route: ActivatedRoute,
    private peopleHRAPIService: PeopleHRAPIService,
    private formBuilder: FormBuilder,
    private swalUtils: SwalUtils
  ) {
    this.breadcrumb = this.breadcrumbService.init(this.route);

    // form - SETUP
    this.setupForm = this.formBuilder.group({
      key: new FormControl('', [Validators.required])
    });
    this.setupForm.controls.key.valueChanges.subscribe(() => {
      this.setupApiKeyInvalid = null; // remove flag if user has changed value in textbox
    });

  }

  ngOnInit() {
    forkJoin([
      this.peopleHRAPIService.getSyncToken(),
      this.peopleHRAPIService.getApiKey()
    ]).subscribe(([syncToken, apiKey]) => {
      this.syncToken = syncToken;
      this.apiKey = apiKey;
      if (this.apiKey && syncToken) {
        this.state = WorkflowState.COMPLETE;
      }
    }, (failure: HttpErrorResponse) => {
    });
  }

  ngOnDestroy() {
    this.breadcrumbService.remove(this.breadcrumb);
  }

  startSetup(): void {
    this.loadingSetup = true;

    // Get webhook url token
    this.peopleHRAPIService.enableSyncing()
      .pipe(finalize(() => this.loadingSetup = false))
      .subscribe(syncToken => {
        this.syncToken = syncToken;
        this.state = WorkflowState.SETUP;
      }, (failure: HttpErrorResponse) => {
      });
  }

  timeoutCheckmarkIcon(): void {
    setTimeout(() => {
      this.syncTokenCopied = false;
    }, 1000);
  }

  timeoutCheckmarkIconApiKey(): void {
    setTimeout(() => {
      this.apiKeyCopied = false;
    }, 1000);
  }

  timeoutCheckmarkIconWebhook(): void {
    setTimeout(() => {
      this.webhookUrlCopied = false;
    }, 1000);
  }

  completeSetup(): void {
    this.setupSubmitted = true;

    if (this.setupForm.invalid) {
      return;
    }

    this.setupCompleting = true;

    const key = this.setupForm.controls.key.value;
    this.testApiKey(key).then(error => {
      // display error message
      if (error) {
        this.setupApiKeyInvalid = error;
        this.setupCompleting = false;
      } else {
        this.peopleHRAPIService.addApiKey(key)
          .pipe(finalize(() => this.setupCompleting = false))
          .subscribe(APIKey => {
            this.apiKey = APIKey;
            this.state = WorkflowState.SYNCING;
            this.manualSync();
          }, (failure: HttpErrorResponse) => {
          });
      }
    });
  }

  disableSync(): void {
    this.swalUtils.displayWarningConfirmationSwal({
      title: 'Disable Syncing?',
      text: 'Users will no longer by Synced with People HR and will need to be manually updated in Frankli by your Admin'
    }).then((response) => {
      if (response.isConfirmed) {
        this.loadingDisableSync = true;
        forkJoin([
          this.peopleHRAPIService.disableSycning(),
          this.peopleHRAPIService.removeApiKey()
        ]).pipe(finalize(() => this.loadingDisableSync = false))
          .subscribe(() => {
            this.syncToken = null;
            this.apiKey = null;
            this.state = WorkflowState.INITIAL;
            this.setupForm.controls.key.setValue('');
            this.setupSubmitted = false;
            this.setupApiKeyInvalid = null;
            this.setupCompleting = false;
          }, (failure: HttpErrorResponse) => {
          });
      }
    });
  }

  /**
   * Test People HR API key, returns null if valid, otherwise returns an error message
   */
  async testApiKey(key: string): Promise<string | null> {
    const response = await window.fetch('https://api.peoplehr.net/v3.1/Employee', {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
      },
      body: JSON.stringify(
        {
          "APIKey": key,
          "Action": "GetAllEmployeeDetail"
        }
      ),
    });

    const data: PeopleHRAPIResponse = await response.json()
    if (response.ok) {
      if (data.isError === false) {
        return null;
      } else {
        switch (data.Status) {
          case PeopleHRAPIResponseStatus.INVALID_API_KEY:
            return 'Invalid API Key';
          case PeopleHRAPIResponseStatus.INVALID_CREDENTIALS:
            return 'API Key valid but invalid credentials, ensure API Key has correct permissions enabled';
          default:
            return 'An unknown error occurred while checking API Key';
        }
      }
    } else {
      return 'Could not contact the People HR API, Please try again later';
    }

  }

  manualSync(): void {
    this.peopleHRAPIService.performSync()
      .pipe(finalize(() => this.loadingManualSync = false))
      .subscribe(response => {
        this.state = WorkflowState.COMPLETE;
        this.swalUtils.displaySuccessSwal({
          title: 'Successfully synced users with People HR',
        });
      }, (failure: HttpErrorResponse) => {
        this.state = WorkflowState.COMPLETE;
      });
  }

  performManualSync(): void {
    if (this.loadingManualSync === true) {
      return;
    }

    this.swalUtils.displayWarningConfirmationSwal({
      title: 'Sync Frankli with People HR?',
    }).then(result => {
      if (result.isConfirmed) {
        this.loadingManualSync = true;
        this.peopleHRAPIService.performSync()
          .pipe(finalize(() => this.loadingManualSync = false))
          .subscribe(() => {
            this.swalUtils.displaySuccessSwal({ title: 'Manual sync successful' })
          }, (failure: HttpErrorResponse) => {

          });
      }
    })
  }

  startUpdate(): void {
    this.state = WorkflowState.UPDATE;
    this.setupForm.controls.key.setValue('');
    this.setupSubmitted = false;
    this.setupApiKeyInvalid = null;
    this.setupCompleting = false;
  }

  completeUpdate(): void {
    this.state = WorkflowState.COMPLETE;
  }

  cancelUpdate(): void {
    this.state = WorkflowState.COMPLETE;
  }

}